import React, { useContext } from 'react';
import { SiteContext } from '../../../../context/SiteContext';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { PageAbsolutePath } from '../../../../consts/Common';
import Table, { HeaderRow, TableRow } from '../../../common/Widgets/Table';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/free-solid-svg-icons/faCheck';
import { faXmark } from '@fortawesome/free-solid-svg-icons/faXmark';
import List from '../../../common/Widgets/List';
import Image from '../../../common/Widgets/Image';
import Links from '../../../common/Widgets/Links';
import ButtonTabs, { QuoteButtonTab } from '../../../common/Widgets/ButtonTabs';
import MediaQuery from 'react-responsive';
import {
  InsuranceJourneyContext,
  InsuranceJourneyState
} from '../../../common/CommonTypes';
import useCarInsuranceController from '../../../../hooks/insuranceJourney/useCarInsuranceController';
import { getStartQuotePath } from '../InsuranceMethods';
import { useErrorBoundary } from 'react-error-boundary';

const GenesisLandingPage = (): JSX.Element => {
  const navigate = useNavigate();
  const { showBoundary } = useErrorBoundary();

  const { site } = useContext(SiteContext);

  const { pageEvents } = useOutletContext<InsuranceJourneyContext>();

  const { commonEvents } = pageEvents;

  const { setQuote } = commonEvents;

  const { getNewQuote } = useCarInsuranceController();

  const getNewQuoteSuccess = (quote: InsuranceJourneyState) => {
    setQuote({
      ...quote,
      tiaProductCode: site.brand.tiaProductCode
    });

    navigate(PageAbsolutePath.Insurance.AboutYou);
  };

  const getNewQuoteAlternateSuccess = (quote: InsuranceJourneyState) => {
    setQuote({
      ...quote,
      tiaProductCode: site.brand.tiaProductCode
    });

    navigate(
      getStartQuotePath(
        site.user.isDealer ?? false,
        site.brand.useAlternateEligibility
      )
    );
  };

  const onBackendFailure = (err: string) => {
    showBoundary(new Error(err));
  };

  const getStarted = () => {
    getNewQuote(
      site.brand.brandName,
      site.user?.insuranceDealerCode ?? '',
      site.user.token,
      getNewQuoteSuccess,
      onBackendFailure
    );
  };

  const getStartedAlternate = () => {
    getNewQuote(
      site.brand.brandName,
      site.user?.insuranceDealerCode ?? '',
      site.user.token,
      getNewQuoteAlternateSuccess,
      onBackendFailure
    );
  };

  const getRetrieveQuote = () => {
    navigate(PageAbsolutePath.Insurance.RetrieveQuote);
  };

  const genesisTableHeader: HeaderRow = {
    columns: ['Genesis Guaranteed', 'Genesis Insurance', 'Top UK insurers']
  };

  const tickComponent = <FontAwesomeIcon icon={faCheck} />;

  const crossComponent = <FontAwesomeIcon icon={faXmark} />;

  const genesisTableRows: TableRow[] = [
    { columns: ['Genesis genuine parts', tickComponent, crossComponent] },
    {
      columns: ['Genesis approved repairers', tickComponent, crossComponent]
    },
    {
      columns: ['Genesis trained technicians', tickComponent, crossComponent]
    },
    {
      columns: [
        'Genesis warranty and safety rating preserved by only fitting Genesis genuine parts',
        tickComponent,
        crossComponent
      ]
    }
  ];

  const standardCoverItems: string[] = [
    'Comprehensive UK & EU cover',
    'Courtesy car (subject to availability)',
    '24-hour accident recovery',
    'Replacement lock cover',
    '5 year repair guarantee',
    'Medical cover',
    'Personal accident cover',
    'Personal possessions cover',
    'Windscreen damage cover',
    'A brand new Genesis if yours is written off or stolen within 12 months of its new registration date'
  ];

  const extraCoverItems: string[] = [
    '£100,000 legal expenses',
    'No-claim discount protection',
    'Upgrade your courtesy car and stay mobile if your car is written-off or stolen',
    'Lost or stolen key cover up to £1,000'
  ];

  const insuranceLinks = [
    { to: './', text: 'Retrieve a quote' },
    { to: './', text: 'Manage my policy' },
    { to: './', text: 'Help' }
  ];

  const informationLinks = [
    {
      to: PageAbsolutePath.Insurance.FairUseNoticePage,
      text: 'Fair use notice'
    },
    { to: './', text: 'Update preferences' },
    {
      to: PageAbsolutePath.Insurance.SiteMap,
      text: 'Site map'
    },
    {
      to: PageAbsolutePath.Insurance.BusinessTermsAncillaryPage,
      text: 'Terms of business'
    }
  ];

  const buttonTabs: QuoteButtonTab[] = [
    {
      buttonText: 'Already own your Genesis?',
      description:
        'Give your Genesis the protection it deserves. Get a competitive 12 month quote today.',
      getQuoteText: 'Get a quote',
      onGetQuoteClick: getStarted,
      retrieveQuoteText: 'Retrieve quote',
      onRetrieveQuoteClick: getRetrieveQuote
    },
    {
      buttonText: 'Awaiting delivery of your Genesis?',
      description: `As soon as you receive your new Genesis, we'll give you complimentary Driveaway Insurance plus a free one-year Excess Return Voucher which can save you up to £250 if you ever need to make a motor insurance claim.`,
      getQuoteText: 'Get a quote',
      onGetQuoteClick: getStartedAlternate,
      retrieveQuoteText: 'Retrieve quote',
      onRetrieveQuoteClick: getRetrieveQuote
    }
  ];

  return (
    <div className='insurance-landing-page'>
      <div className='hero-image'>
        <div className='hero-inner-border'>
          <div className='image-text'>
            <h1>
              Genesis insurance. <br />
              Every detail has been considered
            </h1>
            <ButtonTabs id='hero-buttons' tabs={buttonTabs} />
          </div>
        </div>
      </div>
      <div className='flex-row justify-center'>
        <div className='section-two-image'>
          <MediaQuery maxWidth={1200}>
            <Image
              id='section-2-image'
              source='./brands/genesis/section-two-mobile.jpg'
              altText='Car mirror'
            />
          </MediaQuery>
          <MediaQuery minWidth={1201}>
            <Image
              id='section-2-image'
              source='./brands/genesis/section-two.png'
              altText='Car mirror'
              width='480px'
            />
          </MediaQuery>
        </div>
        <div className='section-two items-center'>
          <div>
            <h2>The cover your Genesis deserves</h2>
            <p>
              We set out to make owning a Genesis effortless. By combining
              beautifully designed cars with a seamless insurance solution we
              have created a car ownership experience that is second to none.
              Our insurance policy offers you the best customer experience and
              puts you in the complete control of your policy and payments.
            </p>
            <p>
              Our exclusive repair benefits guarantee what many insurers don't.
            </p>
            <Table
              id='landing-page-table'
              header={genesisTableHeader}
              rows={genesisTableRows}
              addendum='Source: Research of UK motor insurer policy wordings, August 2024.'
            />
          </div>
        </div>
      </div>
      <Image
        id='section-3-image'
        source='./brands/genesis/section-three.png'
        altText='Thinking image'
        overlayText={
          <span>
            Think all insurers are the same?
            <br />
            Think again.
          </span>
        }
      />
      <div className='flex-row'>
        <div className='section-four'>
          <List
            id='standard-cover'
            title='Standard cover'
            listItems={standardCoverItems}
          />
        </div>
        <div className='section-four-image'>
          <Image
            id='section-4-image'
            source='./brands/genesis/section-four.png'
            altText='Trim image'
          />
        </div>
      </div>
      <div className='section-five'>
        <List
          id='extra-cover'
          title='Optional extra cover'
          listItems={extraCoverItems}
        />
      </div>
      <div className='flex-row justify-center'>
        <Links id='insuranceLinks' title='Insurance' items={insuranceLinks} />
        <Links
          id='informationLinks'
          title='Information'
          items={informationLinks}
        />
      </div>
    </div>
  );
};

export default GenesisLandingPage;
